import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../lib/auth.store';
import { TriDot } from '../ui/TriDot';
import { Icon, type IconComponent } from '../ui/Icons';

interface NavItem {
  to: string;
  label: string;
  icon: IconComponent;
  end?: boolean;
}

const NAV: NavItem[] = [
  { to: '/', label: 'Dashboard', icon: Icon.Home, end: true },
  { to: '/agent', label: 'Agent', icon: Icon.Sparkle },
  { to: '/knowledge', label: 'Knowledge', icon: Icon.Book },
  { to: '/conversations', label: 'Conversations', icon: Icon.Chat },
  { to: '/leads', label: 'Leads', icon: Icon.Check },
];

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

const initials = (name?: string | null, email?: string | null) => {
  const src = name?.trim() || email?.trim() || '';
  if (!src) return '··';
  const parts = src.split(/[\s@.]+/).filter(Boolean);
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? parts[0]?.[1] ?? '')).toUpperCase();
};

export const Layout = () => {
  const user = useAuthStore((s) => s.user);
  const clear = useAuthStore((s) => s.clear);
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    clear();
    navigate('/login', { replace: true });
  };

  const tenantName = user?.tenant?.name ?? 'Workspace';
  const tenantInitial = tenantName.charAt(0).toUpperCase() || 'W';
  const userInitials = initials(user?.name, user?.email);

  const crumbLabel =
    NAV.find((n) => (n.end ? location.pathname === n.to : location.pathname.startsWith(n.to)))?.label ?? 'Dashboard';

  return (
    <div className="min-h-screen flex bg-bg">
      <aside className="w-[232px] shrink-0 bg-white border-r border-line flex flex-col">
        <div className="px-[22px] pt-[22px] pb-[18px]">
          <img
            src="/replai-logo-black-animated.svg?v=1"
            alt="replai"
            className="h-11"
          />
        </div>

        <div className="px-[14px] pb-1.5 flex items-center justify-between">
          <span className="text-[10.5px] font-medium tracking-wide12 uppercase text-ink-subtle">
            Workspace
          </span>
          <TriDot size={4} gap={3} tone="muted" />
        </div>
        <div className="mx-3 mb-3.5 px-3 py-2.5 rounded-[10px] border border-line flex items-center gap-2.5">
          <div
            className="w-[26px] h-[26px] rounded-[7px] flex items-center justify-center text-white font-serif text-[15px] leading-none bg-gradient-tri"
          >
            {tenantInitial}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[12.5px] font-medium text-ink leading-tight truncate">{tenantName}</div>
            <div className="text-[10.5px] text-ink-subtle mt-px truncate">
              {user?.tenant?.planTier ? capitalize(user.tenant.planTier) : 'Workspace'}
              {user?.tenant?.status && user.tenant.status !== 'ACTIVE' && (
                <> · {user.tenant.status.toLowerCase()}</>
              )}
            </div>
          </div>
        </div>

        <nav className="px-2.5 flex flex-col gap-0.5 flex-1">
          {NAV.map(({ to, label, icon: ItemIcon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                `flex items-center gap-[11px] px-2.5 py-[7px] rounded-lg text-[13px] font-medium transition-colors ${
                  isActive
                    ? 'bg-[#F2F2F5] text-ink'
                    : 'text-ink-muted hover:bg-bg'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <span className={isActive ? 'text-accent' : 'text-ink-subtle'}>
                    <ItemIcon size={16} />
                  </span>
                  <span className="flex-1">{label}</span>
                  {isActive && <TriDot size={3.5} gap={2.5} tone="mixed" />}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="p-3 border-t border-line">
          <div className="flex items-center gap-2.5 px-2 py-2 rounded-lg">
            <div className="w-7 h-7 rounded-full bg-ink text-white flex items-center justify-center text-[11px] font-semibold">
              {userInitials}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-xs text-ink font-medium leading-tight">
                {user?.name ?? 'Account'}
              </div>
              <div className="text-[10.5px] text-ink-subtle leading-tight truncate">{user?.email}</div>
            </div>
            <button
              type="button"
              onClick={handleLogout}
              aria-label="Sign out"
              className="text-ink-subtle hover:text-ink transition-colors"
            >
              <Icon.Logout size={14} />
            </button>
          </div>
        </div>
      </aside>

      <main className="flex-1 min-w-0 flex flex-col">
        <TopBar tenantName={tenantName} crumb={crumbLabel} />
        <div className="flex-1 min-h-0 overflow-auto">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

const TopBar = ({ tenantName, crumb }: { tenantName: string; crumb: string }) => (
  <div className="h-12 shrink-0 bg-white border-b border-line flex items-center justify-between pl-10 pr-5">
    <div className="flex items-center gap-2.5 text-xs">
      <span className="text-ink-muted">{tenantName}</span>
      <span className="text-ink-subtle">/</span>
      <span className="text-ink-soft font-medium">{crumb}</span>
    </div>
    <div className="flex items-center gap-2.5">
      <div className="hidden sm:flex items-center gap-2 px-2.5 py-1.5 border border-line rounded-lg text-xs text-ink-subtle w-[220px]">
        <Icon.Search size={13} />
        <span>Search conversations, leads…</span>
        <span className="ml-auto px-1.5 py-px border border-line rounded text-[10px]">⌘K</span>
      </div>
      <button
        type="button"
        aria-label="Notifications"
        className="relative w-8 h-8 rounded-lg border border-line bg-white text-ink-muted flex items-center justify-center hover:text-ink transition-colors"
      >
        <Icon.Bell size={15} />
        <span className="absolute top-1.5 right-[7px] w-1.5 h-1.5 rounded-full bg-accent" />
      </button>
    </div>
  </div>
);
